import React, { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
interface Photo {
  src: string;
  alt: string;
}
const photos: Photo[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/5KBoiRk6gzf9qjN1dB7bMe/IMG_3304.jpg',
  alt: 'A young girl in a pink dress holding her essay in front of the Good Father Project banner'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rbHni2cVugZhZLoHZMSJWZ/IMG_3305.jpg',
  alt: 'Three generations of a family gathered around a young essay winner'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/24Fk55bK1zbhXvxRfgwjoa/IMG_3306.jpg',
  alt: 'A smiling girl holding her Good Father Project winner certificate'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/s5arXeCVNEvozcFyGdhZ5n/IMG_3307.jpg',
  alt: 'An older student holding her winner certificate beside the essay display'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dtgfyCXjdaxFMFAkmp8QqB/IMG_3309.jpg',
  alt: 'A father and daughter holding her certificate and a Fatherhood Matters foam finger'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2CVjSLk35fJKerTT1e9AfJ/IMG_3312.jpg',
  alt: 'Children making Father\u2019s Day cards at the Good Father Project table downtown'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/eFayi5muf1oBonSgECm9NV/IMG_3313.jpg',
  alt: 'Families gathered at the Good Father Project table during Best Weekend Ever'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/tfkFgdRhqrpCcYy1cRGSWn/IMG_3315.jpg',
  alt: 'A father and daughter with her certificate and a gift bag from our sponsor'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rifhgmtvLLs8VQoj4ZYgbq/IMG_3317.jpg',
  alt: 'A son standing between his father and grandfather holding his winner certificate'
}];
export function GallerySection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isOpen = activeIndex !== null;
  const close = useCallback(() => setActiveIndex(null), []);
  const next = useCallback(() => setActiveIndex(i => i === null ? i : (i + 1) % photos.length), []);
  const prev = useCallback(() => setActiveIndex(i => i === null ? i : (i - 1 + photos.length) % photos.length), []);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close, next, prev]);
  return <section id="gallery" className="py-24 bg-white" data-id="element-64" data-mp="qozvzjtqhoql">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-65" data-mp="qqaxte3mcmf2">
        <div className="max-w-3xl mb-12" data-id="element-66" data-mp="vom9ludh56zx">
          <p className="text-xs font-semibold text-[#d4956a] uppercase tracking-wider mb-3" data-id="element-67" data-mp="vnkmusn3iw2d">
            June 2026 &middot; Downtown Harrisonburg
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#3b2316] mb-6" data-id="element-68" data-mp="2b7k3kvkmldvv">
            Moments from the Celebration
          </h2>
          <p className="text-xl text-[#5a3a28]/80 leading-relaxed" data-id="element-69" data-mp="2pxj1cx89qnat">
            Proud daughters and sons visited with their fathers, mothers, and
            grandparents. Winners ranged from 3rd grade to 12th grade, and it
            was clear how happy the children were to celebrate the people who
            show up for them. Many fathers and grandfathers shook our hands and
            said, "Thank you for doing this."
          </p>
        </div>

        <ul className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-id="element-70" data-mp="33vz8uqdyghu0">
          {photos.map((photo, index) => <li key={photo.src} data-id="element-71" data-mp="1efztgrjqtw5x" data-mpi={index}>
              <button type="button" onClick={() => setActiveIndex(index)} className="group block w-full overflow-hidden rounded-xl bg-[#ecdfd2]/30 shadow-sm ring-1 ring-[#ecdfd2] focus:outline-none focus:ring-2 focus:ring-[#c06a2b] focus:ring-offset-2" aria-label={`View photo: ${photo.alt}`} data-id="element-72" data-mp="1eh1fts67m52y">
                <img src={photo.src} alt={photo.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" data-id="element-73" data-mp="1ky2yh5ar1r0n" />
              </button>
            </li>)}
        </ul>

        <p className="mt-8 text-base text-[#5a3a28]/70 italic max-w-3xl" data-id="element-74" data-mp="x5co30ihvfsh">
          With gratitude to our sponsors, judges, and school partners &mdash;
          and to every family who joined us.
        </p>
      </div>

      {/* Lightbox */}
      {isOpen && activeIndex !== null && <div role="dialog" aria-modal="true" aria-label="Photo viewer" className="fixed inset-0 z-[100] flex items-center justify-center bg-[#3b2316]/95 p-4 sm:p-8" onClick={close} data-id="element-75" data-mp="9hmdyxkc8k6x">
          <button type="button" onClick={close} aria-label="Close photo viewer" className="absolute top-4 right-4 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white" data-id="element-76" data-mp="2npx1x661erbw">
            <X size={28} data-id="element-77" data-mp="28swkpwlh2ero" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        prev();
      }} aria-label="Previous photo" className="absolute left-2 sm:left-6 p-3 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white" data-id="element-78" data-mp="28to325q26g00">
            <ChevronLeft size={32} data-id="element-79" data-mp="1q0k88h13tx3t" />
          </button>

          <figure className="max-w-4xl w-full" onClick={e => e.stopPropagation()} data-id="element-80" data-mp="1l327vf15kq7f">
            <img src={photos[activeIndex].src} alt={photos[activeIndex].alt} className="w-full max-h-[75vh] object-contain rounded-lg shadow-2xl" data-id="element-81" data-mp="1bewx1lcvq2x3" />
            <figcaption className="mt-4 text-center text-sm text-white/70" data-id="element-82" data-mp="12f9w10o2az4k">
              {photos[activeIndex].alt}
              <span className="block mt-1 text-white/40" data-id="element-83" data-mp="16gv2o4a06h6b">
                {activeIndex + 1} of {photos.length}
              </span>
            </figcaption>
          </figure>

          <button type="button" onClick={e => {
        e.stopPropagation();
        next();
      }} aria-label="Next photo" className="absolute right-2 sm:right-6 p-3 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white" data-id="element-84" data-mp="1z5aiuu4vf5j8">
            <ChevronRight size={32} data-id="element-85" data-mp="2hv8q580ugult" />
          </button>
        </div>}
    </section>;
}